import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useQueryParam, StringParam } from 'use-query-params';

import { loadAgentPhoto, setCurrentAgent } from '@redux/modules/agent';
import { loadFormDefinition } from '@redux/modules/formGeneration';
import { getAgentSelected, selectAgentPreferredPhoto, selectAgentPreferredPhotoDetails } from '@redux/selectors/AgentSelector';
import { addTeamMember, removeTeamMember, searchTeamMember } from '@redux/modules/teamsManagement';
import { selectCanBeImpersonatedBy, selectCanImpersonate } from '@redux/selectors/TeamsManagementSelector';

import useRowLock from '@hooks/useRowLock';
import useAcl from '@hooks/useAcl';

import TeamMembersTable from '@components/table/TeamMembersTable';
import TeamLeadersTable from '@components/table/TeamLeadersTable';
import AgentLookupModal from '@components/form/formComponents/inputGroups/agentLookup/AgentLookupModal';
import Button from '@components/smartAdminUI/Button';
import Panel from '@components/smartAdminUI/Panel';
import ViewLoader from '@components/loaders/ViewLoader';

import Swal from 'sweetalert2';
import Avatar from '@mui/material/Avatar';
import StatusBadges from '@components/badges/StatusBadges';
import sortBy from 'lodash/sortBy';
import isEmpty from 'lodash/isEmpty';

const operationType = 'edit';
const definitionType = 'agent';

const agentAttributesWhiteList = [
  'CellPhone',
  'CompanyName',
  'PrimaryWebsite',
  'TerminationDate',
  'MemberFax',
  'MemberAddress1',
  'MemberCity',
  'MemberPostalCode',
  'MemberPreferredPhone',
  'MemberPreferredPhoneExt',
  'OfficePhone',
];

const ViewAgents = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const currAgent = useSelector(getAgentSelected);
  const currentAgentPhotoDetails = useSelector(selectAgentPreferredPhotoDetails);
  const currentAgentPhoto = useSelector(selectAgentPreferredPhoto);
  const canBeImpersonatedByData = useSelector(selectCanBeImpersonatedBy);
  const canImpersonateData = useSelector(selectCanImpersonate);
  const memoizedAgentEntries = useMemo(() => (currAgent ? Object.entries(currAgent) : []), [currAgent]);
  const memberName = useMemo(() => currAgent?.MemberFullName ?? currAgent?.MemberFirstName.concat(' ', currAgent?.MemberLastName), [currAgent]);

  const [currentAgentPhotoURL, setCurrentAgentPhotoURL] = useState(null);
  const [editButtonClicked, setEditButtonClicked] = useState(false);

  const [urlParam, setUrlParam] = useQueryParam('UniqueID', StringParam);

  const jsonBody = {
    action: 'select',
    where: {
      clause: [],
    },
  };

  const rowLockEntity = useMemo(() => ({ type: 'agent', id: currAgent?.UniqueID }), [currAgent]);

  const {
    lockingAuthorized,
    locked,
    lockedBy,
    lockedByMe,
    lastUpdatedOn,
    lastCheckedOn,
    checkStatus,
    createLock,
    removeLock,
    takeoverLock,
    discardChanges,
    LockMessage,
    LockOwnerMessage,
  } = useRowLock({ rowLockEntity });

  const getCurrentAgentPhoto = photoURL => {
    dispatch(loadAgentPhoto(photoURL));
  };

  const onEditAgent = () => {
    if (currAgent) {
      dispatch(loadFormDefinition(operationType, definitionType, null, currAgent.UniqueID, 'agentACL'));
    }
  };

  const onClickEdit = () => {
    setEditButtonClicked(true);
    createLock(onEditAgent);
  };

  const {
    getAgentAcl,
    canEditForm,
    canAddTeamMember, canAddToTeam, canViewTeam, canEditTeam,
    isAllComplete,
  } = useAcl();
  const isAllAclComplete = isAllComplete();

  useEffect(() => {
    if (urlParam) {
      jsonBody.where.clause.push({ name: 'UniqueID', value: urlParam });
      dispatch(setCurrentAgent(jsonBody));
    }
  }, []);

  /*
    This has to be done using location. Currently, when the user loads a form, it redirects the user to the
      create/edit page. When this happens, it clears out the path and search values in the location object.
      We can't memo the location and use that as a comparison, because technically the locations will be
      the same on the reload, so we can't reset the search value in the location object.
  */
  useEffect(() => {
    if (location?.search === '') {
      if (currAgent) {
        setUrlParam(currAgent?.UniqueID, 'replaceIn');
      }
    }
  }, [location]);

  useEffect(() => {
    if (currAgent?.UniqueID) {
      // Retrieve the teams details for current agent.
      dispatch(searchTeamMember({ targetMemberkey: currAgent.UniqueID }));

      // retrieves EDIT and COPY permissions for agent
      getAgentAcl({
        action: 'view',
        formname: 'Agent',
        uniqueId: currAgent.UniqueID,
      });

      // retrieves form permissions for agent
      getAgentAcl({
        action: 'forms',
        formname: 'Agent',
        uniqueId: currAgent.UniqueID,
      });

      if (location?.search === '' || location?.search !== currAgent?.UniqueID) {
        setUrlParam(currAgent?.UniqueID, 'replaceIn');
      }
    }
  }, [currAgent]);

  useEffect(() => {
    if (currentAgentPhotoDetails?.URL) {
      getCurrentAgentPhoto(currentAgentPhotoDetails.URL);
    }
  }, [currentAgentPhotoDetails]);

  useEffect(() => {
    if (currentAgentPhotoURL) URL.revokeObjectURL(currentAgentPhotoURL);
    if (currentAgentPhoto) setCurrentAgentPhotoURL(URL.createObjectURL(currentAgentPhoto));
    return () => currentAgentPhotoURL && URL.revokeObjectURL(currentAgentPhotoURL);
  }, [currentAgentPhoto]);

  const onAddMemberToCurrentAgentTeam = async member => {
    if (!(currAgent?.UniqueID && member?.UniqueID)) return;

    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      html: 'Adding Member...',
      title: '<span class="fa-stack fa-2x text-info m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-user fa-stack-1x"></i></span>',
      showConfirmButton: false,
    });

    try {
      const success = await dispatch(addTeamMember({ targetMemberkey: member.UniqueID, targetTeamKey: currAgent.UniqueID }));

      if (success) {
        Swal.fire({
          position: 'center',
          title: 'Member Added',
          icon: 'success',
          iconColor: 'var(--theme-success)',
          showConfirmButton: false,
          timer: 3000,
        });
        // Refresh the teams details for current agent.
        dispatch(searchTeamMember({ targetMemberkey: currAgent.UniqueID }));
      } else {
        Swal.fire({
          allowEnterKey: false,
          allowEscapeKey: false,
          allowOutsideClick: false,
          position: 'center',
          title: 'Member Not Added',
          html: `${member.MemberFullName} could not be added at this time. Please contact support.`,
          icon: 'error',
          iconColor: 'var(--theme-danger)',
          confirmButtonText: 'OK',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotAdded',
          },
        });
        // eslint-disable-next-line no-console
        console.error('Error: View Agent onAddMemberToCurrentAgentTeam', `${member.MemberFullName} Could not be added. Please contact support.`);
      }
    } catch (err) {
      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        title: 'Member Not Added',
        html: `${member.MemberFullName} could not be added at this time. Please contact support.`,
        icon: 'error',
        iconColor: 'var(--theme-danger)',
        confirmButtonText: 'OK',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotAdded',
        },
      });
      // eslint-disable-next-line no-console
      console.error('Error: View Agent onAddMemberToCurrentAgentTeam', err);
    }
  };

  const onAddCurrentAgentToTeam = async member => {
    if (!(currAgent?.UniqueID && member?.UniqueID)) return;

    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      html: 'Adding Member...',
      title: '<span class="fa-stack fa-2x text-info m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-user fa-stack-1x"></i></span>',
      showConfirmButton: false,
    });

    try {
      const success = await dispatch(addTeamMember({ targetMemberkey: currAgent.UniqueID, targetTeamKey: member.UniqueID }));

      if (success) {
        Swal.fire({
          position: 'center',
          title: 'Member Added',
          icon: 'success',
          iconColor: 'var(--theme-success)',
          showConfirmButton: false,
          timer: 3000,
        });
        // Refresh the teams details for current agent.
        dispatch(searchTeamMember({ targetMemberkey: currAgent.UniqueID }));
      } else {
        Swal.fire({
          allowEnterKey: false,
          allowEscapeKey: false,
          allowOutsideClick: false,
          position: 'center',
          title: 'Member Not Added',
          html: `${member.MemberFullName} could not be added at this time. Please contact support.`,
          icon: 'error',
          iconColor: 'var(--theme-danger)',
          confirmButtonText: 'OK',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotAdded',
          },
        });
        // eslint-disable-next-line no-console
        console.error('Error: View Agent onAddCurrentAgentToTeam', `${currAgent.MemberFullName} Could not be added. Please contact support.`);
      }
    } catch (err) {
      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        title: 'Member Not Added',
        html: `${member.MemberFullName} could not be added at this time. Please contact support.`,
        icon: 'error',
        iconColor: 'var(--theme-danger)',
        confirmButtonText: 'OK',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotAdded',
        },
      });
      // eslint-disable-next-line no-console
      console.error('Error: View Agent onAddCurrentAgentToTeam', err);
    }
  };

  const onRemoveMemberFromCurrentAgentTeam = async member => {
    if (!(currAgent?.UniqueID && member?.TeamMemberKeyNumeric)) return;

    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      html: 'Removing Member...',
      title: '<span class="fa-stack fa-2x text-info m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-user fa-stack-1x"></i></span>',
      showConfirmButton: false,
    });

    try {
      const success = await dispatch(removeTeamMember({ targetMemberkey: member.TeamMemberKeyNumeric, targetTeamKey: currAgent.UniqueID }));

      Swal.fire({
        position: 'center',
        title: 'Member Removed',
        icon: 'success',
        iconColor: 'var(--theme-success)',
        showConfirmButton: false,
        timer: 3000,
      });

      if (success) {
        // Refresh the teams details for current agent.
        dispatch(searchTeamMember({ targetMemberkey: currAgent.UniqueID }));
      } else {
        Swal.fire({
          allowEnterKey: false,
          allowEscapeKey: false,
          allowOutsideClick: false,
          position: 'center',
          title: 'Member Not Removed',
          html: `${member.MemberFullName} could not be removed at this time. Please contact support.`,
          icon: 'error',
          iconColor: 'var(--theme-danger)',
          confirmButtonText: 'OK',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotRemoved',
          },
        });
        // eslint-disable-next-line no-console
        console.error('Error: View Agent onRemoveMemberFromCurrentAgentTeam', `${member.MemberFullName} Could not be removed. Please contact support.`);
      }
    } catch (err) {
      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        title: 'Member Not Removed',
        html: `${member.MemberFullName} could not be removd at this time. Please contact support.`,
        icon: 'error',
        iconColor: 'var(--theme-danger)',
        confirmButtonText: 'OK',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotRemoved',
        },
      });
      // eslint-disable-next-line no-console
      console.error('Error: View Agent onRemoveMemberFromCurrentAgentTeam', err);
    }
  };

  const onRemoveCurrentAgentFromTeam = async member => {
    if (!(currAgent?.UniqueID && member?.TeamLeadKeyNumeric)) return;

    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      html: 'Removing Member...',
      title: '<span class="fa-stack fa-2x text-info m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-user fa-stack-1x"></i></span>',
      showConfirmButton: false,
    });

    try {
      const success = await dispatch(removeTeamMember({ targetMemberkey: currAgent.UniqueID, targetTeamKey: member.TeamLeadKeyNumeric }));

      if (success) {
        // Refresh the teams details for current agent.
        Swal.fire({
          position: 'center',
          title: 'Member Removed',
          icon: 'success',
          iconColor: 'var(--theme-success)',
          showConfirmButton: false,
          timer: 3000,
        });
        dispatch(searchTeamMember({ targetMemberkey: currAgent.UniqueID }));
      } else {
        Swal.fire({
          allowEnterKey: false,
          allowEscapeKey: false,
          allowOutsideClick: false,
          position: 'center',
          title: 'Member Not Removed',
          html: `${member.MemberFullName} could not be removed at this time. Please contact support.`,
          icon: 'error',
          iconColor: 'var(--theme-danger)',
          confirmButtonText: 'OK',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotRemoved',
          },
        });
        // eslint-disable-next-line no-console
        console.error('Error: View Agent onRemoveCurrentAgentFromTeam', `${currAgent.MemberFullName} Could not be removed. Please contact support.`);
      }
    } catch (err) {
      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        title: 'Member Not Removed',
        html: `${member.MemberFullName} could not be removed at this time. Please contact support.`,
        icon: 'error',
        iconColor: 'var(--theme-danger)',
        confirmButtonText: 'OK',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger btn-lg swal-fail-agent-memberNotRemoved',
        },
      });
      // eslint-disable-next-line no-console
      console.error('Error: View Agent onRemoveCurrentAgentFromTeam', err);
    }
  };

  if (currAgent) {
    return (
      <>
        <div className="subheader">
          <h1 className="subheader-title">
            <i className="subheader-icon fal fa-user mr-2" />
            View
            <span className="fw-300 ml-2 mr-2">Agent</span>
          </h1>
        </div>
        {!isAllAclComplete && !canEditForm ? (
          <ViewLoader type={definitionType} />
        ) : (
          <>
            {locked && lockingAuthorized && !editButtonClicked && (
              <LockOwnerMessage onRemoveLock={removeLock} />
            )}
            {locked && !lockingAuthorized && (
              <LockMessage
                lockedBy={lockedBy}
                lockedByMe={lockedByMe}
                lastUpdatedOn={lastUpdatedOn}
                lastCheckedOn={lastCheckedOn}
                checkStatus={checkStatus}
                onTakeover={takeoverLock}
                onDiscard={discardChanges}
                displayActions={canEditForm}
              />
            )}
            <div className={locked ? 'row disabled' : 'row'}>
              <div className="col-lg-4 d-flex">
                <Panel customClass="w-100">
                  <div className="d-flex d-column align-items-center justify-content-center p-4">
                    <Avatar
                      sx={{
                        maxWidth: '250px',
                        height: '250px',
                        width: '100%',
                        bgcolor: 'var(--theme-secondary)',
                      }}
                      alt={memberName}
                      src={currentAgentPhotoURL}
                    />
                  </div>
                  <h3 className="mb-0 text-center">{memberName}</h3>
                  <h4 className="mb-0 text-center">{currAgent.OfficeName}</h4>
                  <p className="mt-3 mb-3 text-center">
                    <StatusBadges status={currAgent.MemberStatus} />
                    <span className="ml-2">{currAgent.MemberMlsId}</span>
                  </p>
                  { !isEmpty(currAgent.MemberDirectPhone) && (
                    <p className="mb-0 text-center mt-2">
                      <a href={`phone: ${currAgent.MemberDirectdPhone} `}>
                        <i className="far fa-phone-rotary mr-2" />
                        {currAgent.MemberDirectPhone}
                      </a>
                    </p>
                  )}
                  { !isEmpty(currAgent.MemberEmail) && (
                  <p className="mb-0 text-center mt-0">
                    <a href={`mailto: ${currAgent.MemberEmail} `}>
                      <i className="far fa-envelope mr-2" />
                      {currAgent.MemberEmail}
                    </a>
                  </p>
                  )}
                </Panel>
              </div>
              <div className="col-lg-8 d-flex">
                <Panel
                  header="Agent Details"
                  customClass="w-100"
                  panelToolbar={(
                    <>
                      {canEditForm && (
                        <Button
                          name="btnEditAgent"
                          variant="primary"
                          onClick={onClickEdit}
                          disabled={locked}
                        >
                          <i className="far fa-edit mr-2" />
                          Edit
                        </Button>
                      )}
                    </>
                  )}
                >
                  <table className="table borderless">
                    <tbody>
                      {sortBy(memoizedAgentEntries).map(([key, value]) => {
                        if (!agentAttributesWhiteList.includes(key)) return null;
                        if (value) {
                          return (
                            <tr key={key}>
                              <th className="w-50">{key.replace(/([a-z])([A-Z])/g, '$1 $2')}</th>
                              <td>{value}</td>
                            </tr>
                          );
                        }
                        return null;
                      })}
                      {memoizedAgentEntries.length === 0 && 'No Values to Show'}
                    </tbody>
                  </table>
                </Panel>
              </div>
            </div>
            <div className={locked ? 'row disabled' : 'row'}>
              {canViewTeam && (
                <>
                  {canEditTeam && (
                    <>
                      <AgentLookupModal
                        id="canBeImpersonatedByLookupModal"
                        onChange={onAddMemberToCurrentAgentTeam}
                        getCurrentImpersonationData
                      />
                      <AgentLookupModal
                        id="canImpersonateLookupModal"
                        onChange={onAddCurrentAgentToTeam}
                        getCurrentImpersonationData
                      />
                    </>
                  )}
                  <div className="col-lg-6 d-flex">
                    <Panel
                      customClass="w-100"
                      header="My Impersonations"
                      panelToolbar={(
                        canAddToTeam && (
                          <Button
                            name="btnAddImpersonationsAgent"
                            variant="primary"
                            data-toggle="modal"
                            data-target="#canImpersonateLookupModal"
                            disabled={locked}
                          >
                            <i className="far fa-user-plus mr-2" aria-hidden="true" />
                            Add
                          </Button>
                        )
                      )}
                    >
                      <p>Who this agent can impersonate:</p>
                      <TeamLeadersTable
                        displayData={canImpersonateData}
                        onMemberDelete={onRemoveCurrentAgentFromTeam}
                      />
                    </Panel>
                  </div>
                  <div className="col-lg-6 d-flex">
                    <Panel
                      customClass="w-100"
                      header="My Impersonators"
                      panelToolbar={(
                        canAddTeamMember && (
                          <Button
                            name="btnAddImpersonatorsAgent"
                            variant="primary"
                            data-toggle="modal"
                            data-target="#canBeImpersonatedByLookupModal"
                            disabled={locked}
                          >
                            <i className="far fa-user-plus mr-2" aria-hidden="true" />
                            Add
                          </Button>
                        )
                      )}
                    >
                      <p>Who can impersonate this agent:</p>
                      <TeamMembersTable
                        displayData={canBeImpersonatedByData}
                        onMemberDelete={onRemoveMemberFromCurrentAgentTeam}
                      />
                    </Panel>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </>
    );
  }
  return null;
};

export default ViewAgents;
